/**
 * Every TanStack Query key in the product, in one place.
 *
 * Keys are written here and nowhere else, and lint enforces it: an inline `queryKey: ['ledger']` fails the
 * build (`no-inline-query-keys`). The reason is invalidation. A key typed by hand in one file and
 * mistyped in another produces a cache that never invalidates — the user adds an entry, the list does not
 * change, and nothing anywhere reports an error. That bug is close to undebuggable from the outside, and
 * this file is the whole defence against it.
 *
 * The shape is hierarchical so that a broad invalidation works: invalidating `ledger.all` clears every
 * ledger query, including every filtered list.
 */
export const queryKeys = {
  session: {
    all: ['session'] as const,
    current: () => [...queryKeys.session.all, 'current'] as const,
  },

  ledger: {
    all: ['ledger'] as const,
    list: (filters: Readonly<Record<string, unknown>> = {}) => [...queryKeys.ledger.all, 'list', filters] as const,
    entry: (publicId: string) => [...queryKeys.ledger.all, 'entry', publicId] as const,
    balance: () => [...queryKeys.ledger.all, 'balance'] as const,

    /** `LED-07`. Under `ledger.all`, so restoring or emptying invalidates the list and the balance too. */
    trash: () => [...queryKeys.ledger.all, 'trash'] as const,
  },

  categories: {
    all: ['categories'] as const,
    list: () => [...queryKeys.categories.all, 'list'] as const,
  },

  budgets: {
    all: ['budgets'] as const,
    forMonth: (year: number, month: number) => [...queryKeys.budgets.all, year, month] as const,
  },

  subscription: {
    all: ['subscription'] as const,
    current: () => [...queryKeys.subscription.all, 'current'] as const,
    invoices: () => [...queryKeys.subscription.all, 'invoices'] as const,
    priceChange: () => [...queryKeys.subscription.all, 'price-change'] as const,
  },

  reports: {
    all: ['reports'] as const,
    forMonth: (year: number, month: number) => [...queryKeys.reports.all, year, month] as const,
  },

  household: {
    all: ['household'] as const,
    current: () => [...queryKeys.household.all, 'current'] as const,
  },

  admin: {
    all: ['admin'] as const,
    users: (filters: Readonly<Record<string, unknown>> = {}) => [...queryKeys.admin.all, 'users', filters] as const,
    audit: (filters: Readonly<Record<string, unknown>> = {}) => [...queryKeys.admin.all, 'audit', filters] as const,
  },
} as const;

export type QueryKeys = typeof queryKeys;
